import React, { useEffect, useState } from "react";
import { Box, Typography, Container } from "@mui/material";
import { Link } from "react-router-dom";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PropertiesAPI from "../../../api/firebase/PropertiesAPI";

import bodyDetox from "../../../assets/images/properties/body-detox.jpg";
import hairCare from "../../../assets/images/properties/hair-care.jpg";
import beautySkin from "../../../assets/images/properties/beauty-skin.jpg";
import painMassage from "../../../assets/images/properties/pain-massage.jpg";
import healthWellness from "../../../assets/images/properties/health-wellness.jpg";

export const getCssAspectRatio = (ratioStr, fallback = "3 / 2") => {
  if (!ratioStr || typeof ratioStr !== "string") return fallback;
  return ratioStr.trim().replace(":", " / ") || fallback;
};

export const defaultProperties = [
  {
    id: "prop_body_detox",
    label: "Body Detox Care",
    image: bodyDetox,
    link: "/properties/prop_body_detox",
    accent: "#10b981",
    aspectRatio: "3:2",
  },
  {
    id: "prop_hair_care",
    label: "Hair Care",
    image: hairCare,
    link: "/properties/prop_hair_care",
    accent: "#f59e0b",
    aspectRatio: "3:2",
  },
  {
    id: "prop_beauty_skin",
    label: "Beauty & Skin Care",
    image: beautySkin,
    link: "/properties/prop_beauty_skin",
    accent: "#ec4899",
    aspectRatio: "3:2",
  },
  {
    id: "prop_pain_massage",
    label: "Pain & Massage Oils",
    image: painMassage,
    link: "/properties/prop_pain_massage",
    accent: "#f97316",
    aspectRatio: "3:2",
  },
  {
    id: "prop_health_wellness",
    label: "Health & Wellness",
    image: healthWellness,
    link: "/properties/prop_health_wellness",
    accent: "#6366f1",
    aspectRatio: "16:9",
  },
];

const cardBase = {
  display: "block",
  textDecoration: "none",
  borderRadius: "18px",
  overflow: "hidden",
  position: "relative",
  width: "100%",
  boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
  transition: "transform 0.35s ease, box-shadow 0.35s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 18px 44px rgba(0,0,0,0.20)",
    "& .prop-img": { transform: "scale(1.07)" },
    "& .prop-overlay": {
      background:
        "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.22) 55%, transparent 100%)",
    },
    "& .prop-arrow": { opacity: 1, transform: "translateX(0)" },
  },
};

const CardInner = ({ image, label, accent, large = false }) => (
  <>
    {/* photo */}
    <Box
      className="prop-img"
      component="img"
      src={image}
      alt={label}
      sx={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
        transition: "transform 0.45s ease",
      }}
    />

    {/* gradient */}
    <Box
      className="prop-overlay"
      sx={{
        position: "absolute",
        inset: 0,
        background: large
          ? "linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.6) 42%, rgba(0,0,0,0.12) 72%, transparent 100%)"
          : "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.18) 50%, transparent 100%)",
        transition: "background 0.35s ease",
      }}
    />

    {/* accent pill */}
    <Box
      sx={{
        position: "absolute",
        top: large ? { xs: 16, sm: 22 } : 14,
        left: large ? { xs: 16, sm: 22 } : 14,
        px: large ? 1.75 : 1.25,
        py: large ? 0.6 : 0.4,
        borderRadius: "20px",
        bgcolor: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(8px)",
        border: `1.5px solid ${accent || "#10b981"}`,
        display: "flex",
        alignItems: "center",
        gap: 0.7,
      }}
    >
      <Box
        sx={{
          width: large ? 9 : 7,
          height: large ? 9 : 7,
          borderRadius: "50%",
          bgcolor: accent || "#10b981",
          flexShrink: 0,
          boxShadow: `0 0 8px ${accent || "#10b981"}`,
        }}
      />
      <Typography
        sx={{
          color: "#fff",
          fontSize: large ? "0.8rem" : "0.68rem",
          fontWeight: 800,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        Explore
      </Typography>
    </Box>

    {/* label */}
    <Box
      sx={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        p: large ? { xs: 2.5, sm: 3.5, md: 4 } : { xs: 2, md: 2.5 },
      }}
    >
      <Typography
        sx={{
          color: "#ffffff",
          fontWeight: 900,
          fontSize: large
            ? { xs: "1.45rem", sm: "1.85rem", md: "2.3rem", lg: "2.55rem" }
            : { xs: "1.05rem", md: "1.3rem" },
          lineHeight: 1.18,
          mb: 1.2,
          letterSpacing: "-0.015em",
          textShadow: "0 2px 12px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.95)",
        }}
      >
        {label}
      </Typography>
      <Box
        className="prop-arrow"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          color: "#ffffff",
          bgcolor: accent || "#10b981",
          px: 2.2,
          py: 0.8,
          borderRadius: "24px",
          fontSize: large ? { xs: "0.85rem", md: "0.95rem" } : "0.78rem",
          fontWeight: 800,
          boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
          opacity: { xs: 0.95, md: 0 },
          transform: { xs: "none", md: "translateX(-8px)" },
          transition: "opacity 0.22s ease, transform 0.22s ease, background-color 0.22s ease",
          "&:hover": {
            bgcolor: "#ffffff",
            color: "#0f2820",
          },
        }}
      >
        Shop Now <ArrowForwardRoundedIcon sx={{ fontSize: large ? 18 : 14 }} />
      </Box>
    </Box>
  </>
);

const normalizeProperties = (items = []) =>
  items
    .filter((p) => p.isActive !== false && (p.imageUrl || p.image))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((p) => {
      const defaultLink = `/properties/${p.id}`;
      return {
        id: p.id,
        label: p.label || p.title || "",
        image: p.imageUrl || p.image,
        link: p.link && p.link !== "/collections" ? p.link : defaultLink,
        accent: p.accent || "#10b981",
        aspectRatio: p.aspectRatio,
      };
    });

const ShopByProperties = ({ enabled: propEnabled, properties: initialProperties } = {}) => {
  const suppliedProperties = normalizeProperties(initialProperties);
  const [properties, setProperties] = useState(
    suppliedProperties.length > 0 ? suppliedProperties : defaultProperties
  );
  const [isSectionEnabled, setIsSectionEnabled] = useState(true);

  useEffect(() => {
    if (Array.isArray(initialProperties)) {
      const normalized = normalizeProperties(initialProperties);
      if (normalized.length > 0) setProperties(normalized);
      return undefined;
    }

    const propertiesAPI = new PropertiesAPI();

    if (typeof propertiesAPI.getSectionSettings === "function") {
      propertiesAPI
        .getSectionSettings()
        .then((res) => {
          if (res && res.success && res.data) {
            const enabled = res.data.isEnabled ?? res.data.enabled ?? true;
            setIsSectionEnabled(Boolean(enabled));
          }
        })
        .catch((err) => {
          console.log("Error loading properties section settings:", err);
        });
    }

    propertiesAPI
      .getProperties()
      .then((res) => {
        if (res && res.success && res.data && res.data.length > 0) {
          const activeProps = normalizeProperties(res.data);

          if (activeProps.length > 0) {
            setProperties(activeProps);
          }
        }
      })
      .catch((err) => {
        console.log("Error loading dynamic properties:", err);
      });
  }, [initialProperties]);

  const shouldRender = propEnabled !== undefined ? propEnabled : isSectionEnabled;

  if (!shouldRender || !properties || properties.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        py: { xs: 5, md: 8 },
        background: "linear-gradient(180deg, #f8faf9 0%, #eef5f1 100%)",
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Heading */}
        <Box sx={{ mb: { xs: 4.5, md: 6 }, textAlign: "center" }}>
          <Typography
            variant="overline"
            sx={{
              display: "inline-block",
              color: "#047857",
              bgcolor: "rgba(16, 185, 129, 0.12)",
              px: 2,
              py: 0.6,
              borderRadius: "20px",
              fontWeight: 800,
              letterSpacing: "0.14em",
              fontSize: { xs: "0.8rem", md: "0.88rem" },
              mb: 1.5,
              textTransform: "uppercase",
            }}
          >
            Explore Our Range
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              color: "#062820",
              fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3.2rem" },
              letterSpacing: "-0.03em",
              lineHeight: 1.15,
            }}
          >
            Shop By Properties
          </Typography>
          <Typography
            sx={{
              mt: 1.25,
              color: "#475569",
              fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.15rem" },
              maxWidth: 620,
              mx: "auto",
              lineHeight: 1.5,
              fontWeight: 500,
            }}
          >
            Targeted Ayurvedic formulations tailored for your health, wellness, and self-care routine
          </Typography>
          <Box
            sx={{
              width: 64,
              height: 4,
              bgcolor: "#10b981",
              borderRadius: 3,
              mx: "auto",
              mt: 2,
            }}
          />
        </Box>

        {/* ── Properties Grid: Max 2 images per row, large viewport scale ── */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            gap: { xs: "16px", sm: "20px", md: "26px", lg: "30px" },
            alignItems: "start",
          }}
        >
          {properties.map((item, idx) => {
            const isSingleLast =
              properties.length % 2 === 1 && idx === properties.length - 1;
            const cardAspect = getCssAspectRatio(
              item.aspectRatio,
              isSingleLast ? "16 / 9" : "3 / 2"
            );
            return (
              <Box
                key={item.id || idx}
                component={Link}
                to={item.link}
                sx={{
                  ...cardBase,
                  width: "100%",
                  height: "auto",
                  aspectRatio: cardAspect,
                  borderRadius: { xs: "18px", sm: "22px" },
                  ...(isSingleLast
                    ? {
                        gridColumn: { sm: "1 / -1" },
                      }
                    : {}),
                }}
              >
                <CardInner
                  image={item.image}
                  label={item.label}
                  accent={item.accent}
                  large={true}
                />
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default ShopByProperties;
